// src/components/communications/EmailItem.js
//
// One sent email in the lead communication timeline. Mirrors MessageItem's card
// styling. Note the body shown here is the rep's custom text only — the full
// rendered HTML lives in Brevo, so there is nothing local to expand.
import React from 'react';
import styled from 'styled-components';
import { FaEnvelope, FaLongArrowAltRight, FaLongArrowAltLeft, FaTag } from 'react-icons/fa';
import { formatDateTime } from '../../modules/communications/utils/formatUtils';

const ItemContainer = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(2)};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;

  &:hover {
    transform: translateX(2px);
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(1)};
`;

const DirectionIndicator = styled.div`
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.theme.typography.fontWeight.medium};

  svg {
    margin-right: ${props => props.theme.spacing(1)};
  }
`;

const StatusBadge = styled.span`
  background-color: ${props => {
    switch (props.status) {
      case 'sent': return props.theme.colors.success + '22';
      case 'failed': return props.theme.colors.error + '22';
      default: return props.theme.colors.primary + '22';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'sent': return props.theme.colors.success;
      case 'failed': return props.theme.colors.error;
      default: return props.theme.colors.primary;
    }
  }};
  padding: ${props => props.theme.spacing(0.5)} ${props => props.theme.spacing(1)};
  border-radius: 12px;
  font-size: 0.8rem;
`;

const Details = styled.div`
  margin: ${props => props.theme.spacing(1)} 0;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const Subject = styled.div`
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing(0.5)};
  word-break: break-word;
`;

const Body = styled.div`
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.95rem;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
`;

const TemplateTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: ${props => props.theme.spacing(1)};
  padding: 3px 9px;
  border-radius: 12px;
  font-size: 0.72rem;
  background: ${props => props.theme.colors.primary}22;
  color: ${props => props.theme.colors.primary};
  border: 1px solid ${props => props.theme.colors.primary}44;
`;

const Timestamp = styled.div`
  font-size: 0.8rem;
  color: ${props => props.theme.colors.textSecondary};
  text-align: right;
  margin-top: ${props => props.theme.spacing(1)};
`;

const EmailItem = ({ email }) => {
  const outbound = email.direction !== 'inbound';

  return (
    <ItemContainer>
      <Header>
        <DirectionIndicator>
          <FaEnvelope />
          {outbound ? 'Email Sent' : 'Email Received'}
          {outbound ? <FaLongArrowAltRight /> : <FaLongArrowAltLeft />}
        </DirectionIndicator>
        {email.status && <StatusBadge status={email.status}>{email.status}</StatusBadge>}
      </Header>

      <Details>{outbound ? `To: ${email.to || '—'}` : `From: ${email.from || '—'}`}</Details>

      {email.subject && <Subject>{email.subject}</Subject>}
      {email.body && <Body>{email.body}</Body>}

      {email.templateName && (
        <TemplateTag>
          <FaTag />
          {email.templateName}
        </TemplateTag>
      )}

      <Timestamp>{formatDateTime(email.timestamp)}</Timestamp>
    </ItemContainer>
  );
};

export default EmailItem;
